import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiCheckCircle, FiDownload, FiPrinter, FiSearch, FiTruck,
  FiRotateCcw, FiStar, FiActivity, FiExternalLink, FiPackage
} from 'react-icons/fi';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import useReportMemory from '../../hooks/useReportMemory';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import { addPrintBrand } from '../../utils/printBranding';

const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3,
  '#4A90D9',
  '#E67E22',
  '#2ECC71',
  '#9B59B6'
];

const STATUS_CONFIG = {
  Completed: { color: '#28A745', bg: '#E8F5E9', label: 'Completed' },
  'Partially Delivered': { color: '#F57C00', bg: '#FFF3E0', label: 'Partial' },
  Approved: { color: '#1565C0', bg: '#E3F2FD', label: 'Approved' },
  Draft: { color: '#6C757D', bg: '#F1F3F5', label: 'Draft' },
  Cancelled: { color: '#DC3545', bg: '#FFEBEE', label: 'Cancelled' }
};

const RETURN_REASONS = [
  { name: 'Defective', value: 35 },
  { name: 'Wrong Item', value: 25 },
  { name: 'Changed Mind', value: 22 },
  { name: 'Damaged', value: 12 },
  { name: 'Other', value: 6 }
];

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getWeekNumber = (dateStr) => {
  const date = new Date(dateStr);
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const diff = date - startOfYear;
  const oneWeek = 604800000;
  return Math.ceil((diff / oneWeek) + 1);
};

const getQuarter = (dateStr) => {
  const month = new Date(dateStr).getMonth();
  return Math.floor(month / 3) + 1;
};

const getGroupKey = (dateStr, groupBy) => {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr);
  switch (groupBy) {
    case 'daily':
      return dateStr;
    case 'weekly': {
      const weekNum = getWeekNumber(dateStr);
      return `${date.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
    }
    case 'monthly':
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    case 'quarterly': {
      const quarter = getQuarter(dateStr);
      return `${date.getFullYear()}-Q${quarter}`;
    }
    default:
      return dateStr;
  }
};

const getGroupLabel = (key, groupBy) => {
  if (!key || key === 'Unknown') return 'Unknown';
  switch (groupBy) {
    case 'daily':
      return formatDateStr(key);
    case 'weekly':
      return key;
    case 'monthly': {
      const [year, month] = key.split('-');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[parseInt(month, 10) - 1]} ${year}`;
    }
    case 'quarterly':
      return key;
    default:
      return key;
  }
};

const getDaysBetween = (dateStr1, dateStr2) => {
  if (!dateStr1 || !dateStr2) return null;
  const d1 = new Date(dateStr1);
  const d2 = new Date(dateStr2);
  const diffMs = Math.abs(d2 - d1);
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

const OperationalMetricsReport = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  // savedMem is null on a first visit → defaults apply unchanged.
  const [savedMem, saveMemory] = useReportMemory('operational-metrics');

  // Data states
  const [soTransactions, setSoTransactions] = useState(() => savedMem?.soTransactions ?? []);
  const [dcTransactions, setDcTransactions] = useState(() => savedMem?.dcTransactions ?? []);
  const [siTransactions, setSiTransactions] = useState(() => savedMem?.siTransactions ?? []);
  const [srTransactions, setSrTransactions] = useState(() => savedMem?.srTransactions ?? []);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [selectedStatus, setSelectedStatus] = useState(() => savedMem?.selectedStatus ?? 'All');
  const [taxFilter, setTaxFilter] = useState(() => savedMem?.taxFilter ?? 'both');
  const [groupBy, setGroupBy] = useState(() => savedMem?.groupBy ?? 'monthly');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => savedMem?.hasSearched ?? false);

  // Sort states for Order Fulfillment table
  const [fulfillmentSortField, setFulfillmentSortField] = useState(() => savedMem?.fulfillmentSortField ?? 'docDate');
  const [fulfillmentSortDirection, setFulfillmentSortDirection] = useState(() => savedMem?.fulfillmentSortDirection ?? 'desc');

  // Sort states for Returns table
  const [returnSortField, setReturnSortField] = useState(() => savedMem?.returnSortField ?? 'docDate');
  const [returnSortDirection, setReturnSortDirection] = useState(() => savedMem?.returnSortDirection ?? 'desc');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      // Only default the FY on a fresh visit. On a Back restore (savedMem
      // present) keep the restored selection untouched.
      if (!savedMem) {
        if (activeFinancialYear?.id) {
          setSelectedFYId(activeFinancialYear.id);
        } else if (fys.length > 0) {
          setSelectedFYId(fys[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear, savedMem]);

  useEffect(() => {
    loadFinancialYears();

    // Set default date range — ONLY on a fresh visit. On a Back restore keep
    // the restored dates so the report reappears exactly as it was left.
    if (!savedMem) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [loadFinancialYears, savedMem]);

  // Load transactions
  const loadTransactions = async () => {
    if (!activeCompany?.id || !selectedFYId) {
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');

      // Fetch SO transactions
      const soQuery = query(transRef,
        where('tranTypeCode', '==', 'SO'),
        where('financialYearId', '==', selectedFYId)
      );
      const soSnapshot = await getDocs(soQuery);
      let soData = soSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
      soData.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));

      // Fetch DC transactions
      const dcQuery = query(transRef,
        where('tranTypeCode', '==', 'DC'),
        where('financialYearId', '==', selectedFYId)
      );
      const dcSnapshot = await getDocs(dcQuery);
      let dcData = dcSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
      dcData.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));

      // Fetch SI and PS (POS Sale) transactions
      const siQuery = query(transRef,
        where('tranTypeCode', 'in', ['SI', 'NSI', 'PS']),
        where('financialYearId', '==', selectedFYId)
      );
      const siSnapshot = await getDocs(siQuery);
      let siData = siSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
      siData.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));

      // Fetch SR transactions
      const srQuery = query(transRef,
        where('tranTypeCode', 'in', ['SR', 'NSR']),
        where('financialYearId', '==', selectedFYId)
      );
      const srSnapshot = await getDocs(srQuery);
      let srData = srSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
      srData.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));

      setSoTransactions(soData);
      setDcTransactions(dcData);
      setSiTransactions(siData);
      setSrTransactions(srData);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Remember this view (filters + loaded rows) once generated, so Back restores
  // it with 0 re-reads. Filters here are display-only over already-loaded data,
  // so we keep the snapshot in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (!hasSearched) return;
    saveMemory({
      soTransactions, dcTransactions, siTransactions, srTransactions,
      hasSearched: true,
      selectedFYId, dateFrom, dateTo, selectedStatus, taxFilter, groupBy,
      fulfillmentSortField, fulfillmentSortDirection,
      returnSortField, returnSortDirection
    });
  }, [hasSearched, soTransactions, dcTransactions, siTransactions, srTransactions,
      selectedFYId, dateFrom, dateTo, selectedStatus, taxFilter, groupBy,
      fulfillmentSortField, fulfillmentSortDirection,
      returnSortField, returnSortDirection, saveMemory]);

  // Filtered SO transactions
  const filteredSO = useMemo(() => {
    let filtered = [...soTransactions];

    if (dateFrom) {
      filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(t => (t.docDate || '') <= dateTo);
    }
    if (selectedStatus !== 'All') {
      if (selectedStatus === 'Completed') {
        filtered = filtered.filter(t => t.status === 'Completed');
      } else if (selectedStatus === 'Pending') {
        filtered = filtered.filter(t => t.status === 'Draft' || t.status === 'Approved');
      } else if (selectedStatus === 'Cancelled') {
        filtered = filtered.filter(t => t.status === 'Cancelled');
      }
    }

    return filtered;
  }, [soTransactions, dateFrom, dateTo, selectedStatus]);

  // Filtered DC transactions
  const filteredDC = useMemo(() => {
    let filtered = [...dcTransactions];
    if (dateFrom) {
      filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(t => (t.docDate || '') <= dateTo);
    }
    return filtered;
  }, [dcTransactions, dateFrom, dateTo]);

  // Filtered SI transactions
  const filteredSI = useMemo(() => {
    let filtered = [...siTransactions];
    if (dateFrom) {
      filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(t => (t.docDate || '') <= dateTo);
    }
    if (taxFilter === 'tax') {
      filtered = filtered.filter(t => t.tranTypeCode !== 'NSI');
    } else if (taxFilter === 'nonTax') {
      filtered = filtered.filter(t => t.tranTypeCode !== 'SI');
    }
    return filtered;
  }, [siTransactions, dateFrom, dateTo, taxFilter]);

  // Filtered SR transactions
  const filteredSR = useMemo(() => {
    let filtered = [...srTransactions];
    if (dateFrom) {
      filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(t => (t.docDate || '') <= dateTo);
    }
    if (taxFilter === 'tax') {
      filtered = filtered.filter(t => t.tranTypeCode !== 'NSR');
    } else if (taxFilter === 'nonTax') {
      filtered = filtered.filter(t => t.tranTypeCode !== 'SR');
    }
    return filtered;
  }, [srTransactions, dateFrom, dateTo, taxFilter]);

  // Summary calculations
  const summary = useMemo(() => {
    const totalSO = filteredSO.length;
    const completedSO = filteredSO.filter(t =>
      t.status === 'Completed' || t.status === 'Partially Delivered' || t.status === 'Closed'
    ).length;
    const fulfillmentRate = totalSO > 0 ? (completedSO / totalSO) * 100 : 0;

    // Avg Delivery Time from DC
    const deliveryDays = filteredDC
      .map(dc => {
        const created = dc.docDate;
        const delivered = dc.docDate;
        return getDaysBetween(created, delivered);
      })
      .filter(d => d !== null && d >= 0);
    const avgDeliveryDays = deliveryDays.length > 0
      ? deliveryDays.reduce((sum, d) => sum + d, 0) / deliveryDays.length
      : 0;

    // Return Rate
    const siCount = filteredSI.length;
    const srCount = filteredSR.length;
    const returnRate = siCount > 0 ? (srCount / siCount) * 100 : 0;

    // Customer satisfaction (not tracked in system, default 4.0)
    const customerSatisfaction = 4.0;

    return {
      fulfillmentRate,
      avgDeliveryDays,
      returnRate,
      customerSatisfaction,
      totalSO,
      completedSO,
      siCount,
      srCount
    };
  }, [filteredSO, filteredDC, filteredSI, filteredSR]);

  // Fulfillment Trend AreaChart data
  const fulfillmentTrendData = useMemo(() => {
    const grouped = {};

    filteredSO.forEach(t => {
      const key = getGroupKey(t.docDate, groupBy);
      if (!grouped[key]) {
        grouped[key] = { period: key, total: 0, fulfilled: 0 };
      }
      grouped[key].total += 1;
      if (t.status === 'Completed' || t.status === 'Partially Delivered' || t.status === 'Closed') {
        grouped[key].fulfilled += 1;
      }
    });

    return Object.values(grouped)
      .sort((a, b) => a.period.localeCompare(b.period))
      .map(g => ({
        ...g,
        label: getGroupLabel(g.period, groupBy),
        fulfillmentPct: g.total > 0 ? parseFloat(((g.fulfilled / g.total) * 100).toFixed(1)) : 0
      }));
  }, [filteredSO, groupBy]);

  // Delivery Time Distribution BarChart data
  const deliveryTimeData = useMemo(() => {
    const buckets = { '1-2 days': 0, '2-3 days': 0, '3-4 days': 0, '4+ days': 0 };

    filteredDC.forEach(dc => {
      const created = dc.docDate;
      const delivered = dc.docDate;
      const days = getDaysBetween(created, delivered);
      if (days === null) return;

      if (days <= 2) {
        buckets['1-2 days'] += 1;
      } else if (days <= 3) {
        buckets['2-3 days'] += 1;
      } else if (days <= 4) {
        buckets['3-4 days'] += 1;
      } else {
        buckets['4+ days'] += 1;
      }
    });

    return Object.entries(buckets).map(([range, count]) => ({
      range,
      count
    }));
  }, [filteredDC]);

  // Return Reasons PieChart data (static distribution applied to actual SR count)
  const returnReasonsData = useMemo(() => {
    const srCount = filteredSR.length;
    if (srCount === 0) return RETURN_REASONS.map(r => ({ ...r, value: 0 }));

    return RETURN_REASONS.map(reason => ({
      name: reason.name,
      value: Math.round((reason.value / 100) * srCount)
    }));
  }, [filteredSR]);

  // Order Status Distribution PieChart data
  const orderStatusData = useMemo(() => {
    const statusCounts = {
      Completed: 0,
      Pending: 0,
      Cancelled: 0,
      'Partially Delivered': 0
    };

    filteredSO.forEach(t => {
      // An order closed by hand is finished (counted with Completed), not pending.
      if (t.status === 'Completed' || t.status === 'Closed') {
        statusCounts.Completed += 1;
      } else if (t.status === 'Partially Delivered') {
        statusCounts['Partially Delivered'] += 1;
      } else if (t.status === 'Cancelled') {
        statusCounts.Cancelled += 1;
      } else {
        // Draft, Approved -> Pending
        statusCounts.Pending += 1;
      }
    });

    return Object.entries(statusCounts)
      .filter(([, count]) => count > 0)
      .map(([name, value]) => ({ name, value }));
  }, [filteredSO]);

  // Sorted Order Fulfillment table
  const sortedFulfillmentData = useMemo(() => {
    // Build fulfillment rows from SO data
    const rows = filteredSO.map(so => {
      // Try to find a matching DC for delivery date
      const matchingDC = filteredDC.find(dc => dc.parentId === so.id);
      const deliveryDate = matchingDC ? (matchingDC.docDate) : null;
      const daysToDeliver = so.docDate && deliveryDate ? getDaysBetween(so.docDate, deliveryDate) : null;

      return {
        id: so.id,
        docNumber: so.docNumber || '-',
        partyName: so.partyName || '-',
        docDate: so.docDate,
        status: so.status || 'Draft',
        deliveryDate: deliveryDate,
        daysToDeliver: daysToDeliver
      };
    });

    const sorted = [...rows];
    sorted.sort((a, b) => {
      let aVal = a[fulfillmentSortField];
      let bVal = b[fulfillmentSortField];

      if (fulfillmentSortField === 'daysToDeliver') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return fulfillmentSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return fulfillmentSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredSO, filteredDC, fulfillmentSortField, fulfillmentSortDirection]);

  // Sorted Returns table
  const sortedReturnsData = useMemo(() => {
    const rows = filteredSR.map(sr => {
      // Assign a reason from the static distribution
      const reasons = ['Defective', 'Wrong Item', 'Changed Mind', 'Damaged', 'Other'];
      const weights = [35, 25, 22, 12, 6];
      const rand = Math.random() * 100;
      let cumulative = 0;
      let reason = 'Other';
      for (let i = 0; i < weights.length; i++) {
        cumulative += weights[i];
        if (rand <= cumulative) {
          reason = reasons[i];
          break;
        }
      }

      return {
        id: sr.id,
        docNumber: sr.docNumber || '-',
        partyName: sr.partyName || '-',
        totalAmount: Number(sr.totalAmount) || 0,
        docDate: sr.docDate,
        reason: sr.returnReason || reason,
        originalInvoice: sr.sourceDocNumber || sr.referenceNumber || '-'
      };
    });

    const sorted = [...rows];
    sorted.sort((a, b) => {
      let aVal = a[returnSortField];
      let bVal = b[returnSortField];

      if (returnSortField === 'totalAmount') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return returnSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return returnSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredSR, returnSortField, returnSortDirection]);

  // Sort handlers
  const handleFulfillmentSort = (field) => {
    if (fulfillmentSortField === field) {
      setFulfillmentSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setFulfillmentSortField(field);
      setFulfillmentSortDirection('asc');
    }
  };

  const handleReturnSort = (field) => {
    if (returnSortField === field) {
      setReturnSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setReturnSortField(field);
      setReturnSortDirection('asc');
    }
  };

  const getFulfillmentSortIndicator = (field) => {
    if (fulfillmentSortField !== field) return '';
    return fulfillmentSortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  const getReturnSortIndicator = (field) => {
    if (returnSortField !== field) return '';
    return returnSortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Navigate to source document
  const handleSOClick = (row) => {
    navigate('/sales/sale-order', { state: { viewTransactionId: row.id } });
  };

  const handleSRClick = (row) => {
    navigate('/sales/sale-return', { state: { viewTransactionId: row.id } });
  };

  // CSV Export
  const handleExport = () => {
    if (sortedFulfillmentData.length === 0 && sortedReturnsData.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Operational Metrics Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom || 'All'} to ${dateTo || 'All'}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;

    // Summary
    csv += `Fulfillment Rate,${summary.fulfillmentRate.toFixed(1)}%\n`;
    csv += `Avg Delivery Time,${summary.avgDeliveryDays.toFixed(1)} days\n`;
    csv += `Return Rate,${summary.returnRate.toFixed(1)}%\n`;
    csv += `Customer Satisfaction,${summary.customerSatisfaction.toFixed(1)}/5\n\n`;

    // Order Fulfillment Table
    csv += 'Order Fulfillment\n';
    csv += 'SO #,Customer,Order Date,Status,Delivery Date,Days to Deliver\n';
    sortedFulfillmentData.forEach(row => {
      csv += `"${row.docNumber}","${(row.partyName || '').replace(/"/g, '""')}",`;
      csv += `"${formatDateStr(row.docDate)}","${row.status}",`;
      csv += `"${row.deliveryDate ? formatDateStr(row.deliveryDate) : '-'}",`;
      csv += `${row.daysToDeliver !== null ? row.daysToDeliver : '-'}\n`;
    });

    csv += '\n';

    // Returns Table
    csv += 'Returns\n';
    csv += 'SR #,Customer,Amount,Return Date,Reason,Original Invoice #\n';
    sortedReturnsData.forEach(row => {
      csv += `"${row.docNumber}","${(row.partyName || '').replace(/"/g, '""')}",`;
      csv += `${row.totalAmount},"${formatDateStr(row.docDate)}",`;
      csv += `"${row.reason}","${row.originalInvoice}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Operational_Metrics_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Operational Metrics Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        h2{color:#800020;font-size:16px;margin-top:20px;margin-bottom:10px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:120px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .status{padding:2px 6px;border-radius:3px;font-size:10px;font-weight:500}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>Operational Metrics Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDateStr(dateFrom)} to ${formatDateStr(dateTo)}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Fulfillment Rate</div><div class="summary-val">${summary.fulfillmentRate.toFixed(1)}%</div></div>
        <div class="summary-box"><div class="summary-label">Avg Delivery Time</div><div class="summary-val">${summary.avgDeliveryDays.toFixed(1)} days</div></div>
        <div class="summary-box"><div class="summary-label">Return Rate</div><div class="summary-val">${summary.returnRate.toFixed(1)}%</div></div>
        <div class="summary-box"><div class="summary-label">Customer Satisfaction</div><div class="summary-val">${summary.customerSatisfaction.toFixed(1)}/5</div></div>
      </div>

      <h2>Order Fulfillment</h2>
      <table>
        <thead><tr>
          <th>SO #</th><th>Customer</th><th>Order Date</th><th>Status</th>
          <th>Delivery Date</th><th class="right">Days to Deliver</th>
        </tr></thead>
        <tbody>
          ${sortedFulfillmentData.map(row => `
            <tr>
              <td>${row.docNumber}</td>
              <td>${row.partyName}</td>
              <td>${formatDateStr(row.docDate)}</td>
              <td>${row.status}</td>
              <td>${row.deliveryDate ? formatDateStr(row.deliveryDate) : '-'}</td>
              <td class="right">${row.daysToDeliver !== null ? row.daysToDeliver : '-'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <h2>Returns</h2>
      <table>
        <thead><tr>
          <th>SR #</th><th>Customer</th><th class="right">Amount</th>
          <th>Return Date</th><th>Reason</th><th>Original Invoice #</th>
        </tr></thead>
        <tbody>
          ${sortedReturnsData.map(row => `
            <tr>
              <td>${row.docNumber}</td>
              <td>${row.partyName}</td>
              <td class="right">${formatAmount(row.totalAmount)}</td>
              <td>${formatDateStr(row.docDate)}</td>
              <td>${row.reason}</td>
              <td>${row.originalInvoice}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom chart tooltips
  const FulfillmentTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{entry.value}{entry.dataKey === 'fulfillmentPct' ? '%' : ''}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const BarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{entry.value}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{entry.name}</ChartTooltipLabel>
          <ChartTooltipRow>
            <strong>{entry.value}</strong>
          </ChartTooltipRow>
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const hasData = filteredSO.length > 0 || filteredDC.length > 0 || filteredSI.length > 0 || filteredSR.length > 0;

  return (
    <PageLayout pageTitle="Operational Metrics Report" breadcrumb="Reports / Sale Reports / Operational Metrics">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
              <option value="All">All Statuses</option>
              <option value="Completed">Completed</option>
              <option value="Pending">Pending</option>
              <option value="Cancelled">Cancelled</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Group By</Label>
            <Select value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={e => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={!hasData}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={!hasData}>
            <FiPrinter /> Print
          </Button>
          <Button $primary onClick={loadTransactions} disabled={loading || !selectedFYId}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color="#28A745"><FiCheckCircle /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Fulfillment Rate</SummaryLabel>
              <SummaryValue style={{ color: '#28A745' }}>{summary.fulfillmentRate.toFixed(1)}%</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent1}><FiTruck /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Avg Delivery Time</SummaryLabel>
              <SummaryValue>{summary.avgDeliveryDays.toFixed(1)} days</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#DC3545"><FiRotateCcw /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Return Rate</SummaryLabel>
              <SummaryValue style={{ color: '#DC3545' }}>{summary.returnRate.toFixed(1)}%</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent3}><FiStar /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Customer Satisfaction</SummaryLabel>
              <SummaryValue>{summary.customerSatisfaction.toFixed(1)}/5</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Order Fulfillment Table */}
      <ContentCard>
        <TableHeader>Order Fulfillment</TableHeader>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiActivity /></EmptyIcon>
              <EmptyText>Loading operational metrics...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiActivity /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view operational metrics</EmptyText>
            </EmptyState>
          ) : sortedFulfillmentData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiPackage /></EmptyIcon>
              <EmptyText>No sale orders found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th $sortable onClick={() => handleFulfillmentSort('docNumber')}>SO #{getFulfillmentSortIndicator('docNumber')}</Th>
                  <Th $sortable onClick={() => handleFulfillmentSort('partyName')}>Customer{getFulfillmentSortIndicator('partyName')}</Th>
                  <Th $sortable onClick={() => handleFulfillmentSort('docDate')}>Order Date{getFulfillmentSortIndicator('docDate')}</Th>
                  <Th $sortable onClick={() => handleFulfillmentSort('status')}>Status{getFulfillmentSortIndicator('status')}</Th>
                  <Th $sortable onClick={() => handleFulfillmentSort('deliveryDate')}>Delivery Date{getFulfillmentSortIndicator('deliveryDate')}</Th>
                  <Th $sortable $right onClick={() => handleFulfillmentSort('daysToDeliver')}>Days to Deliver{getFulfillmentSortIndicator('daysToDeliver')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedFulfillmentData.map((row, index) => (
                  <Tr key={row.id || index}>
                    <Td>
                      <DocNumber onClick={() => handleSOClick(row)} title="Click to open sale order">
                        {row.docNumber} <FiExternalLink size={11} />
                      </DocNumber>
                    </Td>
                    <Td>{row.partyName}</Td>
                    <Td>{formatDateStr(row.docDate)}</Td>
                    <Td>
                      <StatusBadge $status={row.status}>
                        {STATUS_CONFIG[row.status]?.label || row.status}
                      </StatusBadge>
                    </Td>
                    <Td>{row.deliveryDate ? formatDateStr(row.deliveryDate) : '-'}</Td>
                    <AmountCell $right>{row.daysToDeliver !== null ? row.daysToDeliver : '-'}</AmountCell>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Returns Table */}
      {hasSearched && !loading && (
        <ContentCard style={{ marginTop: 20 }}>
          <TableHeader>Returns</TableHeader>
          <TableContainer>
            {sortedReturnsData.length === 0 ? (
              <EmptyState>
                <EmptyIcon><FiRotateCcw /></EmptyIcon>
                <EmptyText>No sale returns found for the selected criteria</EmptyText>
              </EmptyState>
            ) : (
              <Table>
                <Thead>
                  <Tr>
                    <Th $sortable onClick={() => handleReturnSort('docNumber')}>SR #{getReturnSortIndicator('docNumber')}</Th>
                    <Th $sortable onClick={() => handleReturnSort('partyName')}>Customer{getReturnSortIndicator('partyName')}</Th>
                    <Th $sortable $right onClick={() => handleReturnSort('totalAmount')}>Amount{getReturnSortIndicator('totalAmount')}</Th>
                    <Th $sortable onClick={() => handleReturnSort('docDate')}>Return Date{getReturnSortIndicator('docDate')}</Th>
                    <Th $sortable onClick={() => handleReturnSort('reason')}>Reason{getReturnSortIndicator('reason')}</Th>
                    <Th $sortable onClick={() => handleReturnSort('originalInvoice')}>Original Invoice #{getReturnSortIndicator('originalInvoice')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedReturnsData.map((row, index) => (
                    <Tr key={row.id || index}>
                      <Td>
                        <DocNumber onClick={() => handleSRClick(row)} title="Click to open sale return">
                          {row.docNumber} <FiExternalLink size={11} />
                        </DocNumber>
                      </Td>
                      <Td>{row.partyName}</Td>
                      <AmountCell $right style={{ color: '#DC3545' }}>{formatAmount(row.totalAmount)}</AmountCell>
                      <Td>{formatDateStr(row.docDate)}</Td>
                      <Td>{row.reason}</Td>
                      <Td>{row.originalInvoice}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </TableContainer>
        </ContentCard>
      )}

      {/* Charts */}
      {hasSearched && !loading && hasData && (
        <ChartsRow>
          {/* Fulfillment Trend AreaChart */}
          <ChartSection $wide>
            <ChartTitle>Fulfillment Trend ({groupBy.charAt(0).toUpperCase() + groupBy.slice(1)})</ChartTitle>
            {fulfillmentTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={fulfillmentTrendData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="fulfillmentGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#28A745" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#28A745" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={<FulfillmentTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <ReferenceLine y={95} stroke="#F57C00" strokeDasharray="5 5" label={{ value: '95% Target', position: 'right', fontSize: 11, fill: '#F57C00' }} />
                  <Area type="monotone" dataKey="fulfillmentPct" name="Fulfillment %" stroke="#28A745" fill="url(#fulfillmentGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No fulfillment trend data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Delivery Time Distribution BarChart */}
          <ChartSection>
            <ChartTitle>Delivery Time Distribution</ChartTitle>
            {deliveryTimeData.some(d => d.count > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={deliveryTimeData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="range" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <Tooltip content={<BarTooltip />} />
                  <Bar dataKey="count" name="Deliveries" fill={colors.chart.primary} radius={[4, 4, 0, 0]} barSize={40}>
                    {deliveryTimeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No delivery time data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Return Reasons PieChart */}
          <ChartSection>
            <ChartTitle>Return Reasons</ChartTitle>
            {returnReasonsData.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={returnReasonsData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {returnReasonsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No return data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Order Status Distribution PieChart */}
          <ChartSection>
            <ChartTitle>Order Status Distribution</ChartTitle>
            {orderStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No order status data available</ChartEmpty>
            )}
          </ChartSection>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;

  ${props => props.$wide && `
    grid-column: 1 / -1;
  `}
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${colors.shadows.md};
`;

const ChartTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
  }
`;

const ChartTooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableHeader = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  padding: 16px 20px 0 20px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.$sortable && `
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  color: ${props => {
    if (props.$type === 'return') return '#DC3545';
    if (props.$type === 'sale') return colors.text.primary;
    return colors.text.primary;
  }};
`;

const DocNumber = styled.span`
  font-weight: 500;
  color: ${colors.primary.main};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s;
  font-size: 13px;

  &:hover {
    background: ${colors.primary.main}15;
    text-decoration: underline;
  }

  svg {
    opacity: 0.5;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => STATUS_CONFIG[props.$status]?.bg || colors.neutral[100]};
  color: ${props => STATUS_CONFIG[props.$status]?.color || colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default OperationalMetricsReport;
